import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SnackChip from "../../../components/SnackChip/SnackChip";
import { KeyStorage, getStorageItem } from "../../../utils/localStorage";
import EditPencil from "../../../assets/img/edit-pencil.svg";
import DuplicateSvg from "../../../assets/img/duplicate.svg";
import PublicateSvg from "../../../assets/img/publicate.svg";
import ArchiveSvg from "../../../assets/img/archive.svg";
import FolderSvg from "../../../assets/img/folder.svg";
import EmptyTable from "../../../components/EmptyTable/EmptyTable";
import { RoutesRouter } from "../../../models/Routes";
import Button from "../../../components/Button/Button";
import { CardTemplate } from "../../../components/CardTemplate/CardTemplate";
import { PopulatedTemplate } from "../../../models/SurveyModels";
import { useEndpoints } from "../../../hooks/useEndpoints";
import Skeleton from "react-loading-skeleton";
import { useToastAlert } from "../../../components/ToastAlert/ToastAlert";
import { DURATION_TOAST } from "../../../utils/costants";
import { downloadResponseAsFile } from "../../../utils/functions";

require("./TemplatePage.scss");

export type ITemplateButtonOptions = {
  onClick?: (index: number) => void | Promise<void>;
  isLoading?: boolean;
  colorTheme: "green" | "blue" | "red";
  label: string | JSX.Element | boolean;
  icon: JSX.Element;
}[];

export const TemplatePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getAllTemplate, patchTemplate, exportTemplates } = useEndpoints();
  const [isExporting, setIsExporting] = useState(false);
  const [templates, setTemplate] = useState<PopulatedTemplate[] | undefined>();
  const [inputValue, setInputValue] = React.useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const { showToast } = useToastAlert();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };
  const [filterCategory, setFilterCategory] = useState<
    "" | "ACTIVE" | "DRAFT" | "ARCHIVED"
  >("");
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const response = await exportTemplates({
        name: debouncedInputValue,
        status: filterCategory,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await downloadResponseAsFile(response, "template.xlsx");
    } catch (err) {
      console.error("Export templates failed:", err);
      showToast({
        message: `${t("home.survey.exportError")}`,
        type: "error",
        duration: DURATION_TOAST,
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getAllTemplate({
      name: debouncedInputValue,
      status: filterCategory,
    }).then((resp) => {
      setTemplate(resp);
      setIsLoading(false);
    });
  }, [debouncedInputValue, filterCategory, getAllTemplate]);

  const generateButtons = useCallback(
    (
      callback: (
        value: NonNullable<any>,
        role: KeyStorage["me"]["role"]
      ) => ITemplateButtonOptions
    ): Array<ITemplateButtonOptions> => {
      if (!templates) return [] as Array<ITemplateButtonOptions>;
      return templates.map((company) => {
        return callback(company, getStorageItem("me")!.role);
      });
    },
    [templates]
  );
  const changeKeyById = useCallback(
    (id: string, value: "ACTIVE" | "DRAFT" | "ARCHIVED"): void => {
      patchTemplate(id, value).then((res) => {
        if (res.status === 200) {
          setTemplate((prevTemplate) => {
            if (filterCategory !== "") {
              const updatedTemplate = prevTemplate?.filter(
                (template) => template.id !== id
              );
              return updatedTemplate;
            } else {
              const updatedTemplate = prevTemplate?.map((template) => {
                if (template.id === id) {
                  return {
                    ...template,
                    status: value,
                  };
                }
                return template;
              });
              return updatedTemplate;
            }
          });
          showToast({
            message: `${t(`home.template.toast.success.${value}`)}`,
            type: "success",
            duration: DURATION_TOAST,
          });
        }
      });
    },
    [filterCategory, patchTemplate]
  );
  const buttonsOption = useMemo(
    () =>
      generateButtons((resource: PopulatedTemplate) => {
        let buttons: ITemplateButtonOptions = [];
        resource.status !== "ARCHIVED" &&
          buttons.push(
            {
              onClick: (index: number) => {
                navigate(
                  `${RoutesRouter.template}/${
                    templates && templates[index].id
                  }/edit`
                );
              },
              label: `${t("home.edit")}`,
              colorTheme: "blue",
              icon: <img src={EditPencil} alt="edit" />,
            },
            {
              onClick: (index) => {
                navigate(`${RoutesRouter.template}/create`, {
                  state: { duplicationContent: templates && templates[index] },
                });
              },
              label: `${t("home.survey.actions.duplicate")}`,
              colorTheme: "blue",
              icon: <img src={DuplicateSvg} alt="go" />,
            }
          );
        resource.status !== "DRAFT" &&
          buttons.push({
            onClick: (index) => {
              changeKeyById(resource.id, "DRAFT");
            },
            label: `${t(`home.survey.actions.draft`)}`,
            icon: <img src={ArchiveSvg} alt="archive"></img>,
            colorTheme: "blue",
          });
        resource.status !== "ARCHIVED" &&
          buttons.push({
            onClick: (index) => {
              changeKeyById(resource.id, "ARCHIVED");
            },
            label: `${t(`home.survey.actions.archive`)}`,
            icon: <img src={FolderSvg} alt="archive"></img>,
            colorTheme: "blue",
          });
        resource.status !== "ACTIVE" &&
          buttons.push({
            onClick: (index) => {
              changeKeyById(resource.id, "ACTIVE");
            },
            label: `${t(`home.survey.actions.publicate`)}`,
            icon: <img src={PublicateSvg} alt="archive"></img>,
            colorTheme: "blue",
          });
        return buttons;
      }),
    [generateButtons, t, navigate, templates, changeKeyById]
  );

  const filtersLine = () => (
    <div className="resources-searching container-filters my-3">
      <div className="form-group">
        <input
          id="searchResource"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          className="form-control filter-questions"
          placeholder={t("home.questions.inputPlaceholder") as string}
        />
        <i className="icon-search"></i>
        {inputValue && (
          <i
            className="icon-close"
            onClick={() => {
              setInputValue("");
            }}
          ></i>
        )}
      </div>
      <div className="container-filters">
        {(["", "ACTIVE", "DRAFT", "ARCHIVED"] as const).map((item, index) => (
          <SnackChip
            key={index}
            filters
            handleCLick={() => {
              setFilterCategory(item);
            }}
            isActive={item === filterCategory}
            theme="filters"
            label={t(`surveyTemplate.filters.${item === "" ? "all" : item}`)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="container-custom template-page">
      <div className="container__top">
        <div>
          <h1 className="title-page"> {t("home.survey.template.title")}</h1>
          <p>{t("home.survey.description")}</p>
        </div>
        <div className="mt-3 d-flex gap-2">
          <Button outline onClick={handleExport} disabled={isExporting}>
            {isExporting
              ? t("home.survey.exporting")
              : t("home.survey.exportExcel")}
          </Button>
          <Button outline onClick={() => navigate(RoutesRouter.addTemplate)}>
            {t("surveyTemplate.create.createButton")}
            <span className="plus-char">+</span>
          </Button>
        </div>
      </div>
      {filtersLine()}
      <div className="container-templates">
        {!isLoading && templates && templates.length > 0 && (
          <div className=" row wrapper-template-items">
            <CardTemplate
              templates={templates || []}
              buttonsOption={buttonsOption}
            />
          </div>
        )}
        {!isLoading && templates?.length === 0 && (
          <EmptyTable
            title={`${t(
              `surveyTemplate.filters.${
                filterCategory === "" ? "all" : filterCategory
              }`
            )}`}
            description={t("surveyTemplate.description")}
          />
        )}
        {isLoading && (
          <Skeleton
            count={10}
            width={"24%"}
            height={184}
            inline
            containerClassName="container-template-skeleton"
          />
        )}
      </div>
    </div>
  );
};
